import * as React from "react";
import { Redirect } from "react-router-dom";
import { SettingR } from "../route";
import { SettingsTemplate } from "../../../template/settings/default-template";
import { connectPageService } from "@graffer-inc/smart-apply";
import {
  CopyTextBox,
  PrimaryButton,
  SecondaryButton,
  Spinner,
  TextField,
} from "@graffer-inc/console-design-kit";
import { useAuthContextV2 } from "../../../../auth-state";
import {
  OrgSamlSettingService,
  OrgSamlSettingServiceProps,
} from "../../../../application/service/settings/org-saml-setting/org-saml-setting-service";
import { useModal } from "../../../../utils/custom-hooks";
import { CertificateFileUploadModal } from "../../../organisms/settings/org-saml-setting/certificate-file-upload-modal";
import { OrgSamlSettingValidator } from "../../../../application/domain/organization/org-saml-setting-validator";
import { emptyFunction } from "../../../../utils/function-utils";

export const OrgSamlSettingPage: React.FC<OrgSamlSettingServiceProps> = ({
  isLoading,
  setting,
  mode,
  initSetting,
  setSetting,
  uploadCertificateFile,
  downloadSAMLSPMetadataXML,
  setMode,
  deleteCertificateFile,
  fetchOrgSamlSetting,
  createOrgSamlSetting,
  updateOrgSamlSetting,
}) => {
  const { auth } = useAuthContextV2();
  const certificateFileUploadModal = useModal();

  const validator = React.useMemo(() => {
    return new OrgSamlSettingValidator(setting);
  }, [setting]);

  React.useEffect(() => {
    if (auth.data?.orgId) {
      fetchOrgSamlSetting(auth.data.orgId);
    }
  }, [fetchOrgSamlSetting, auth]);

  if (
    !auth.data?.orgUsingFeature.useSamlAuth ||
    !auth.hasPermissions("org_saml_settings.read")
  ) {
    return <Redirect to={SettingR.MyAccount.link({})} />;
  }

  if (isLoading) {
    return <Spinner loading />;
  }
  if (!setting) {
    return (
      <SettingsTemplate
        breadcrumbs={[
          { to: SettingR.OrgSamlSetting.link({}), title: "SAML SSO設定" },
        ]}
      >
        <div className="columns">
          <div className="column is-8">
            <h1 className="title">
              <i className="fa fa-file-alt" /> SAML SSO設定
            </h1>
            <PrimaryButton
              text="SAML SSO設定を開始する"
              onClick={() => initSetting(auth.data?.orgId!)}
            />
          </div>
        </div>
      </SettingsTemplate>
    );
  }

  return (
    <SettingsTemplate
      breadcrumbs={[
        { to: SettingR.OrgSamlSetting.link({}), title: "SAML SSO設定" },
      ]}
    >
      <div className="columns">
        <div className="column is-8">
          <div className="columns">
            <div className="column">
              <h1 className="title">
                <i className="fa fa-file-alt" /> SAML SSO設定
              </h1>
            </div>
            {mode === "VIEW" ? (
              <div className="column is-narrow">
                <SecondaryButton
                  text={"編集"}
                  onClick={() => setMode("EDIT")}
                />
              </div>
            ) : (
              <>
                <div className="column is-narrow">
                  <SecondaryButton
                    text={"キャンセル"}
                    onClick={() => {
                      deleteCertificateFile();
                      fetchOrgSamlSetting(auth.data?.orgId!);
                      setMode("VIEW");
                    }}
                  />
                </div>
                <div className="column is-narrow">
                  <PrimaryButton
                    text={setting.id !== "" ? "更新" : "作成"}
                    onClick={() => {
                      if (setting.id !== "") {
                        updateOrgSamlSetting(setting);
                      } else {
                        createOrgSamlSetting(setting);
                      }
                    }}
                    disabled={!setting || !validator.isValid()}
                  />
                </div>
              </>
            )}
          </div>
          <h2
            className="subtitle mt-5 pl-2"
            style={{ borderLeft: "6px solid #40bfb0" }}
          >
            SAML SSO設定項目
          </h2>
          <div className="field my-5">
            <TextField
              label={"サービスプロバイダ識別子"}
              description={"このSAML設定の識別子を入力してください。"}
              placeholder={"my-saml-setting"}
              required
              value={setting.samlSlug}
              onChange={(ce) => {
                setSetting({ ...setting, samlSlug: ce.target.value });
              }}
              disabled={mode === "VIEW"}
              errorMessages={validator.samlSlug}
            />
          </div>
          <div className="field my-5">
            <TextField
              label={"IDプロバイダのEntity ID"}
              description={
                "IDプロバイダのEntity IDを入力してください。プロバイダによってはIssuer URLという形で表示される場合があります。"
              }
              placeholder={"http://example.com"}
              required
              value={setting.idProviderEntityId}
              onChange={(ce) => {
                setSetting({
                  ...setting,
                  idProviderEntityId: ce.target.value.trim(),
                });
              }}
              disabled={mode === "VIEW"}
              errorMessages={validator.idProviderEntityId}
            />
          </div>
          <div className="field my-5">
            <TextField
              label={"IDプロバイダのSingle Sign On URL"}
              description={
                "IDプロバイダのSingle Sign OnのURLを入力してください。"
              }
              placeholder={"https://example.com/sso/saml"}
              required
              value={setting.idProviderSsoUrl}
              onChange={(ce) => {
                setSetting({
                  ...setting,
                  idProviderSsoUrl: ce.target.value.trim(),
                });
              }}
              disabled={mode === "VIEW"}
              errorMessages={validator.idProviderSsoUrl}
            />
          </div>
          <div className="field my-5">
            <TextField
              label="X509証明書ファイル"
              description="PEM形式でエンコードされたIDプロバイダのX509証明書ファイルをアップロードしてください。"
              placeholder=""
              required
              value={
                setting.certificateFileKey.length > 0
                  ? "アップロード済み"
                  : "未アップロード"
              }
              onChange={emptyFunction} // noop, since file upload is executed in modal and this field is always disabled
              disabled={true}
              errorMessages={validator.certificateFileKey}
            />
            {mode === "EDIT" && (
              <div className="mt-5">
                <SecondaryButton
                  text="アップロード"
                  onClick={() => certificateFileUploadModal.open()}
                />
              </div>
            )}
          </div>
          <div>
            <div className="spacer" />
            <h2
              className="subtitle mt-5 pl-2"
              style={{ borderLeft: "6px solid #40bfb0" }}
            >
              サービスプロバイダ情報
            </h2>
            <p
              className="mb-2"
              style={{ fontSize: "14px", fontFamily: "Hiragino Sans" }}
            >
              下記の情報を元に ID
              プロパイダで設定してください。メタデータファイルもご利用いただけます。
            </p>
            <PrimaryButton
              text={"メタデータファイルをダウンロード"}
              onClick={async (e) => {
                e.preventDefault();
                await downloadSAMLSPMetadataXML(auth.data?.orgId!);
              }}
            />
            <div className="field my-5">
              <label
                className="mb-2"
                style={{
                  display: "inline-block",
                  fontFamily: "Hiragino Sans",
                  fontSize: "16px",
                  fontWeight: "600",
                }}
              >
                Entity ID
              </label>
              <p
                className="mb-2"
                style={{ fontSize: "14px", fontFamily: "Hiragino Sans" }}
              >
                サービスプロバイダのEntity
                IDです。IDプロバイダ側にこのIDを登録してください。
              </p>
              <CopyTextBox
                value={`https://console.graffer.jp/${auth.data?.orgSlug}`}
              />
            </div>
            <div className="field my-5">
              <label
                className="mb-2"
                style={{
                  display: "inline-block",
                  fontFamily: "Hiragino Sans",
                  fontSize: "16px",
                  fontWeight: "600",
                }}
              >
                ACS (Assertion Consumer Service) URL
              </label>
              <p
                className="mb-2"
                style={{ fontSize: "14px", fontFamily: "Hiragino Sans" }}
              >
                Single Sign
                On成功後にコールバックされるURLです。IDプロバイダ側にこのURLを登録してください。
              </p>
              <CopyTextBox
                value={`https://${window.location.host}/api/saml/callback`}
              />
            </div>
            <div className="field my-5">
              <label
                className="mb-2"
                style={{
                  display: "inline-block",
                  fontFamily: "Hiragino Sans",
                  fontSize: "16px",
                  fontWeight: "600",
                }}
              >
                Relay State
              </label>
              <p
                className="mb-2"
                style={{ fontSize: "14px", fontFamily: "Hiragino Sans" }}
              >
                IdP Initiated SSOを行う場合、IDプロバイダ側にこのRelay
                Stateを登録してください。 なお、Relay StateはSAML
                SSO設定後に表示されます。
              </p>
              <CopyTextBox value={setting.relayState} />
            </div>
          </div>
        </div>
      </div>
      {certificateFileUploadModal.isActive && (
        <CertificateFileUploadModal
          isActive={certificateFileUploadModal.isActive}
          onClose={certificateFileUploadModal.close}
          onUploadFile={uploadCertificateFile}
        />
      )}
    </SettingsTemplate>
  );
};

export const OrgSamlSettingContainer = connectPageService(
  OrgSamlSettingPage,
  () => new OrgSamlSettingService(),
);
